import type {
  CasOrCategory,
  Chemical,
  EmissionsLimitID,
  EmissionsTrackingFrequency,
  EmissionsUnit,
  EmissionsUnitID,
  EmissionsUnitType,
  EmissionsVariableAnswerFrequency,
  ProductChemicalCompositionID,
  ProductID,
  ReleaseProfileID,
  ReleaseProfileQuestionID,
  ReleaseProfileVariableID,
  SimpleSelectable,
  UsageTypeID,
  UsageUnits,
} from './db.ts';
import type { ISO8601DateOnly } from '../../cross-app-shared/types/common.ts';
import type {
  FormulaOutputType,
  ReleaseProfileFormula,
  ReleaseProfileFormulaChoiceID,
  ReleaseProfileFormulaChoiceListID,
  ReleaseProfileFormulaOutput,
  ReleaseProfileVariableDefinition,
  ReleaseProfilesAndVariables,
  FormulaID,
  OutputID,
  PotentialEmissionsBasisPeriod,
} from './release-profiles.ts';
import type { CalculationLine } from '../services/releases-calculate.ts';
import type { EmissionsUnitWithQuestionAndAnswerData } from '../services/reports/emissions-to-table.ts';
import type { EmissionsPeriodDetails } from '../services/emissions/emissions-dates.ts';
import type { Row } from '../services/reports/report-pipelines.ts';

export type EmissionsLimitPollutantSelector =
  | { type: 'explicit'; casOrCategories: CasOrCategory[] }
  | { type: 'classification'; classification: 'all' | 'hap' };

export type EmissionsLimitWindow =
  | { type: 'tracked-period' }
  | { type: 'calendar-month' }
  | { type: 'calendar-year' }
  | { type: 'total' }
  | { type: 'rolling-months'; months: number };

export type EmissionsLimitType =
  | 'regulatory'
  | 'permitting-basis'
  | 'internal-target'
  | 'corrective-action'
  | 'operational-alert';

export type EmissionsLimitChemicalGrouping =
  | 'combined-chemicals'
  | 'each-chemical-single-limit'
  | 'each-chemical-multiple-limits';
export type EmissionsLimitUnitGrouping = 'combined-units' | 'each-unit';
export type EmissionsLimitWindowReduction = 'sum' | 'maximum' | 'ratio-of-totals';
export type EmissionsPollutantLimit = { casOrCategory: CasOrCategory; limitValue: number };

/**
 * Whether a limit measures what a unit actually emitted or what it could emit running at its configured potential.
 * A limit built on a compliance metric measures that metric instead and ignores this — see
 * emissionsLimitAcceptedOutputTypes in services/emissions/emissions-compliance.ts.
 */
export type EmissionsBasis = 'actual' | 'potential';

/** One potential-emissions formula output a potential limit is measured against. */
export interface PotentialEmissionsOutputSelection {
  emissionsUnitID: EmissionsUnitID;
  releaseProfileID: ReleaseProfileID;
  formulaID: FormulaID;
  outputID: OutputID;
}

export type ComplianceMetricLimitDefault<Value> = null | {
  value: Value;
  // If this is true, a facility assigning this compliance metric is not allowed to deviate from the value saved above:
  mandatory: boolean;
};

export interface EmissionsComplianceMetricConfig {
  // This does not affect calculations directly but instead if valueSemantics are set to 'period-average' we only
  // allow users to use the 'maximum' windowReduction as the other options are generally nonsensical with averages:
  valueSemantics: 'period-total' | 'period-average';

  expectedUnits: ComplianceMetricLimitDefault<string>;
  unitGrouping: ComplianceMetricLimitDefault<EmissionsLimitUnitGrouping>;
  chemicalGrouping: ComplianceMetricLimitDefault<EmissionsLimitChemicalGrouping>;
  windowReduction: ComplianceMetricLimitDefault<EmissionsLimitWindowReduction>;
  window: ComplianceMetricLimitDefault<EmissionsLimitWindow>;
}

/**
 * What a limit's compliance metric looked like when the limit was saved: only the parts of the metric that can move
 * the value it produces or invalidate the limit itself. Compared with a freshly built one to report exactly what an
 * admin changed underneath a saved limit — see services/emissions/emissions-metric-snapshot.ts.
 */
export const complianceMetricSnapshotVersion = 1;

export interface ComplianceMetricSnapshotIfChoice {
  choiceListID: ReleaseProfileFormulaChoiceListID;
  choiceID: ReleaseProfileFormulaChoiceID;
}

export interface ComplianceMetricSnapshotOutput {
  type: FormulaOutputType;
  chemicals: ReleaseProfileFormulaOutput['chemicals'];
  ifChoices: ComplianceMetricSnapshotIfChoice[];
}

export interface ComplianceMetricSnapshotFormula {
  nameForVariable: string;
  formula: string;
  /** Only the metric's own compliance outputs; a profile's actual or potential outputs are measured by no limit. */
  outputs: Record<string, ComplianceMetricSnapshotOutput>;
}

export type ComplianceMetricSnapshotVariable =
  | { kind: 'constant'; units: string; value: number }
  | { kind: 'question'; units: string; questionID: ReleaseProfileQuestionID; isChemicalSpecific: boolean }
  | {
      kind: 'chemicalProfile';
      units: string;
      chemicals: Partial<Record<CasOrCategory, number>>;
      usageTypesApplied: UsageTypeID[];
    }
  | { kind: 'predefined'; units: string }
  | { kind: 'derived' }
  | { kind: 'unsupported'; units: string; definitionType: string }
  | { kind: 'missing' };

export interface ComplianceMetricSnapshot {
  version: number;
  releaseProfileID: ReleaseProfileID;
  isArchived: boolean;
  config: EmissionsComplianceMetricConfig;
  formulas: Record<string, ComplianceMetricSnapshotFormula>;
  variables: Record<string, ComplianceMetricSnapshotVariable>;
  choiceLists: Record<string, { choiceIDs: ReleaseProfileFormulaChoiceID[] }>;
  /**
   * Display names, kept apart from everything above and excluded from comparison, so that renaming a variable or an
   * answer never invalidates a limit but is still available to describe a change that did.
   */
  labels: {
    metricName: string;
    formulas: Record<string, string>;
    variables: Record<string, string>;
    choiceLists: Record<string, string>;
    choices: Record<string, string>;
  };
}

export interface EmissionUnitTypeWithConfig extends SimpleSelectable<EmissionsUnitType> {
  unitCount: number;
  releaseProfiles: {
    releaseProfileID: ReleaseProfileID;
    name: string;
    formula: ReleaseProfileFormula;
  }[];
  variableConfigs: {
    releaseProfileVariableID: ReleaseProfileVariableID;
    casOrCategory: CasOrCategory | null;
    frequency: EmissionsVariableAnswerFrequency;
    answer: number | null;
    nameForVariable: string;
    nameFriendly: string;
    units: string;
    description: string;
    definition: ReleaseProfileVariableDefinition;
  }[];
  formulaChoiceConfigs: {
    choiceListID: ReleaseProfileFormulaChoiceListID;
    frequency: EmissionsVariableAnswerFrequency;
    answer: ReleaseProfileFormulaChoiceID | null;
  }[];
  predefinedVariableConfigs: {
    predefinedVariableName: string;
    frequency: EmissionsVariableAnswerFrequency;
  }[];
}

export interface EmissionsLimitListItem {
  emissionsLimitID: EmissionsLimitID;
  name: string;
  limitType: EmissionsLimitType;
  reference: string;
  emissionsUnits: {
    emissionsUnitID: EmissionsUnitID;
    name: string;
    entriesStartAt: ISO8601DateOnly;
    trackingFrequency: EmissionsTrackingFrequency;
  }[];
  pollutantSelector: EmissionsLimitPollutantSelector;
  chemicalGrouping: EmissionsLimitChemicalGrouping;
  unitGrouping: EmissionsLimitUnitGrouping;
  window: EmissionsLimitWindow;
  windowReduction: EmissionsLimitWindowReduction;
  effectiveStartDate: ISO8601DateOnly;
  limitValue: number;
  limitUnit: string;
  pollutantLimits: EmissionsPollutantLimit[];
  releaseProfileID: ReleaseProfileID | null;
  /** Null for a limit measuring emissions rather than a metric, and for one saved before snapshots were recorded. */
  complianceMetricSnapshot: ComplianceMetricSnapshot | null;
  emissionsBasis: EmissionsBasis;
  /** Duration one potential result represents, so it can be scaled to the limit's window. Null unless potential. */
  potentialBasisPeriod: PotentialEmissionsBasisPeriod | null;
  /** Which potential outputs feed this limit. Empty unless potential. */
  potentialOutputSelections: PotentialEmissionsOutputSelection[];
}

// Compare with EmissionsCalculationLineForReport:
export interface EmissionsCalculationLineForDrilldown {
  unit: EmissionsUnitWithQuestionAndAnswerData;
  period: EmissionsPeriodDetails;
  line: CalculationLine;
  values?: Row['values'];
  contextProducts?: { productName: string; amount: number; units: UsageUnits }[];
  /**
   * A potential line's value scaled from the output's own basis period onto the one its limit is capped against, so
   * the drilldown can show the number the limit actually compared. Null for every line that isn't a potential line
   * measured by a potential-basis limit — the value and the basis it was scaled onto are meaningless apart.
   */
  potentialNormalization: { value: number; capBasisPeriod: PotentialEmissionsBasisPeriod } | null;
}

export interface EmissionsLimitNormalizedCalculation {
  emissionsUnitID: EmissionsUnitID;
  emissionsUnitName: string;
  casOrCategory: CasOrCategory | null;
  releaseProfileID: ReleaseProfileID | null;
  formulaOutputType: FormulaOutputType;
  /** Which formula output within the profile produced this line, so a potential limit can select specific outputs. */
  releaseProfileFormulaID: FormulaID | null;
  releaseProfileOutputID: OutputID | null;
  /** Duration this line's value represents, for potential lines whose output declares one. */
  potentialBasisPeriod: PotentialEmissionsBasisPeriod | null;
  value: number;
  units: string;
  periodStart: ISO8601DateOnly;
  periodEnd: ISO8601DateOnly;
  parentPeriodStart: ISO8601DateOnly;
  parentPeriodEnd: ISO8601DateOnly;
  isQuestionnaireComplete: boolean;
  hasCalculationWarnings: boolean;
  drilldown: EmissionsCalculationLineForDrilldown;
}

export interface EmissionsLimitPeriod {
  start: ISO8601DateOnly;
  end: ISO8601DateOnly;
  friendly: string;
}

export type EmissionsLimitEvaluation =
  | {
      status: 'configuration-error';
      message: string;
      period: EmissionsLimitPeriod;
      casOrCategory: CasOrCategory | null;
    }
  | {
      status: 'provisional' | 'compliant' | 'exceeded' | 'not-applicable';
      message: string | null;
      casOrCategories: CasOrCategory[];
      emissionsUnitIds: EmissionsUnitID[];
      period: EmissionsLimitPeriod;
      observedValue: number;
      limitValue: number;
      unit: string;
      percentOfLimit: number;
      calculations: EmissionsLimitNormalizedCalculation[];
    };

export interface EmissionsLimitEvaluationInput {
  limit: EmissionsLimitListItem;
  chemicalsByCasOrCategory: Partial<Record<CasOrCategory, Pick<Chemical, 'isHap'>>>;
  emissions: EmissionsLimitNormalizedCalculation[];
  /**
   * Supplied so a limit whose compliance metric was edited underneath it reports that instead of a number computed
   * from a definition its author never saw. Omitting it evaluates the limit without that check.
   */
  releaseProfilesAndVariables?: ReleaseProfilesAndVariables;
}

export interface EmissionUnitWithConfig extends SimpleSelectable<EmissionsUnit> {
  products: {
    productID: ProductID;
    name: string;
    specificGravity: number | null;
    usageUnits: UsageUnits;
    solidsPercentageByWeight: number | null;
    solidsPercentageByVolume: number | null;
    archivedAt: ISO8601DateOnly | null;
    hasUsage: boolean;
    chemicals: {
      productChemicalCompositionID: ProductChemicalCompositionID;
      casOrCategoryMapped: CasOrCategory; // May be mapped onto a category.
      casOrCategoryOriginal: CasOrCategory; // Will be the originally entered CAS or category on the product.
      concentrationMin: number | null;
      concentrationMax: number | null;
      concentrationSolo: number | null;
      concentrationComputed: number;
      chemicalName: string;
      elementalConversionEligible: boolean;
      volatileOrSolids: '' | 'volatile' | 'solids';
    }[];
  }[];
  variableConfigs: {
    releaseProfileVariableID: ReleaseProfileVariableID;
    casOrCategory: CasOrCategory | null;
    answer: number | null;
    nameForVariable: string;
    nameFriendly: string;
    units: string;
    description: string;
    definition: ReleaseProfileVariableDefinition;
  }[];
  formulaChoiceConfigs: {
    choiceListID: ReleaseProfileFormulaChoiceListID;
    answer: ReleaseProfileFormulaChoiceID;
  }[];
  limitsWithReleaseProfiles: {
    emissionsLimitID: EmissionsLimitID;
    releaseProfileID: ReleaseProfileID;
    name: string;
    formula: ReleaseProfileFormula;
  }[];
}
